// CNC 파트너 — 매출 명세서(제조사 → 기공소) (2026-10-23, UI 구조 단계)
// 흐름: 케이스를 「출고」로 바꾸는 순간 그 건이 명세서 항목으로 쌓임 → 마감일에 기공소별 거래명세서 자동 생성
//       → 기공소 덴클 화면에 표시 → 기공소 「확인」 또는 「이의」 → 양측 확정 → 그때만 계산서 발행(Popbill 재사용 예정)
// 분쟁 장치(문헌 조사 10-23 권고): 이의 상태·양측 확정·수정 이력, 확정 전 계산서 잠금. 과세/면세 미확정 → 「계산서」 중립 표현
// 영업업체가 소개한 기공소도 명세서는 항상 기공소에 직접(대표 10-23 — 영업업체는 제조사가 따로 수수료 정산).
// 단가 = 기공소별 예외 단가가 있으면 그것, 없으면 종류별 기본 단가. 데이터는 샘플(저장 안 됨).
import React, { useMemo, useState } from 'react';
import { FileDown, FileSpreadsheet, Receipt, Send, X, Tag, AlertTriangle, Plus, Trash2, CalendarClock, History, MessageSquareWarning } from 'lucide-react';
import { SAMPLE_PARTNER, SAMPLE_LABS, makeSampleCases } from './partnerSample';
import { won, fmtMd, KindChip, KIND_ORDER, ui } from './partnerUi';
import { MF_KIND_LABEL, detailSummary, teethLabel } from '../../services/outsourceMfApi';

const DAY = 24 * 60 * 60 * 1000;
const BASE_PRICE = { cnc: 55000, metal: 38000, zirconia: 45000, general: 40000, etc: 0 };
const SAMPLE_EXCEPTIONS = [
    { id: 'e1', labId: 'lab1', kind: 'cnc', price: 50000 },   // 물량 많은 기공소 할인 단가
];
// 지난달 상태 예시 — 이번 달은 마감 전이라 전부 「작성 중」
const PREV_STATUS = { lab1: 'confirmed', lab2: 'disputed', lab3: 'confirmed', lab4: 'issued', lab5: 'confirmed' };
// 상태: 작성 중 → 발행(기공소 확인 대기) → 이의 있음 ↔ 수정 재발행 → 양측 확정(이때만 계산서 발행)
const ST = {
    draft: { label: '작성 중', fg: '#475569', bg: '#f1f5f9' },
    issued: { label: '기공소 확인 대기', fg: '#1d4ed8', bg: '#dbeafe' },
    disputed: { label: '이의 있음', fg: '#b91c1c', bg: '#fee2e2' },
    confirmed: { label: '양측 확정', fg: '#047857', bg: '#d1fae5' },
};
// 샘플 — 지난달 이의·수정 이력
const PREV_DISPUTE = { lab2: '재제작으로 다시 받은 1건이 이중으로 들어가 있습니다. 1건 빼 주세요.' };
const historyOf = (labId, status) => {
    const h = [{ at: '10/31 18:00', who: '시스템', text: '마감일 자동 생성 (출고 기준)' }];
    if (status !== 'draft') h.push({ at: '11/01 09:12', who: '제조사', text: '발행 — 기공소에 전송' });
    if (status === 'disputed') h.push({ at: '11/02 14:30', who: '기공소', text: `이의: ${PREV_DISPUTE[labId] || ''}` });
    if (status === 'confirmed') h.push({ at: '11/02 10:05', who: '기공소', text: '확인 — 양측 확정' });
    return h;
};

const notReady = (what) => alert(`미리보기: ${what}은(는) 서버 연결 후 동작합니다.`);
const qtyOf = (c) => (c.detail && c.detail.qty) || 1;
const ymOf = (d) => `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
const lastDayOf = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

function StatementBadge({ status }) {
    const s = ST[status] || ST.draft;
    return <span style={{ ...ui.badge, color: s.fg, backgroundColor: s.bg }}>{s.label}</span>;
}

// ── 단가표 (기본 단가 + 기공소별 예외) ──
function PriceModal({ base, setBase, exceptions, setExceptions, onClose }) {
    const [nLab, setNLab] = useState(SAMPLE_LABS[0].id);
    const [nKind, setNKind] = useState('cnc');
    const [nPrice, setNPrice] = useState('');
    const add = () => {
        const price = parseInt(String(nPrice).replace(/[^0-9]/g, ''), 10);
        if (!price) return;
        setExceptions((p) => [...p.filter((e) => !(e.labId === nLab && e.kind === nKind)), { id: `e${Date.now()}`, labId: nLab, kind: nKind, price }]);
        setNPrice('');
    };
    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={{ ...styles.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHead}>
                    <span style={styles.modalTitle}><Tag size={16} />{'단가표'}</span>
                    <span style={{ flex: 1 }} />
                    <button type="button" style={styles.closeBtn} onClick={onClose} aria-label="닫기"><X size={18} /></button>
                </div>
                <div style={styles.modalBody}>
                    <div style={styles.secTitle}>{'기본 단가 (종류별, VAT 별도)'}</div>
                    {KIND_ORDER.filter((k) => k !== 'etc').map((k) => (
                        <div key={k} style={styles.priceRow}>
                            <KindChip kind={k} />
                            <span style={{ flex: 1 }} />
                            <input
                                style={styles.priceInput}
                                value={base[k].toLocaleString('ko-KR')}
                                onChange={(e) => setBase((p) => ({ ...p, [k]: parseInt(e.target.value.replace(/[^0-9]/g, ''), 10) || 0 }))}
                                inputMode="numeric"
                            />
                            <span style={ui.muted}>{'원'}</span>
                        </div>
                    ))}

                    <div style={{ ...styles.secTitle, marginTop: 18 }}>{'기공소별 예외 단가'}</div>
                    <p style={ui.cardSub}>{'예외 단가가 있으면 기본 단가 대신 적용됩니다.'}</p>
                    {exceptions.length === 0 && <span style={ui.muted}>{'예외 단가 없음'}</span>}
                    {exceptions.map((e) => (
                        <div key={e.id} style={styles.priceRow}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{(SAMPLE_LABS.find((l) => l.id === e.labId) || {}).name}</span>
                            <KindChip kind={e.kind} />
                            <span style={{ flex: 1 }} />
                            <b style={{ fontSize: 13 }}>{won(e.price)}</b>
                            <button type="button" style={styles.iconBtn} onClick={() => setExceptions((p) => p.filter((x) => x.id !== e.id))} aria-label="삭제"><Trash2 size={13} /></button>
                        </div>
                    ))}
                    <div style={styles.addRow}>
                        <select style={ui.select} value={nLab} onChange={(e) => setNLab(e.target.value)}>
                            {SAMPLE_LABS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        </select>
                        <select style={ui.select} value={nKind} onChange={(e) => setNKind(e.target.value)}>
                            {KIND_ORDER.filter((k) => k !== 'etc').map((k) => <option key={k} value={k}>{MF_KIND_LABEL[k]}</option>)}
                        </select>
                        <input style={{ ...styles.priceInput, flex: 1, minWidth: 90 }} placeholder="단가" value={nPrice} onChange={(e) => setNPrice(e.target.value)} inputMode="numeric" />
                        <button type="button" style={ui.primaryBtn} onClick={add}><Plus size={14} />{'추가'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── 거래명세서 문서 미리보기 ──
function StatementDoc({ lab, items, month, status, priceOf, onClose, mobile }) {
    const history = historyOf(lab.id, status);
    const canInvoice = status === 'confirmed';
    const supply = items.reduce((s, c) => s + priceOf(c) * qtyOf(c), 0);
    const vat = Math.round(supply * 0.1);
    const docNo = `ST-${month.getFullYear()}${String(month.getMonth() + 1).padStart(2, '0')}-${lab.code}`;
    return (
        <div style={styles.overlay} onClick={onClose}>
            <div style={{ ...styles.modal, maxWidth: 860 }} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHead}>
                    <span style={styles.modalTitle}>{`${lab.name} — ${ymOf(month)} 거래명세서`}</span>
                    <StatementBadge status={status} />
                    <span style={{ flex: 1 }} />
                    <button type="button" style={styles.closeBtn} onClick={onClose} aria-label="닫기"><X size={18} /></button>
                </div>

                <div style={styles.modalBody}>
                    {/* 문서 */}
                    <div style={styles.doc}>
                        <div style={styles.docTitle}>{'거 래 명 세 서'}</div>
                        <div style={styles.docMeta}>
                            <span>{`No. ${docNo}`}</span>
                            <span>{`작성일 ${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(lastDayOf(month).getDate()).padStart(2, '0')}`}</span>
                        </div>
                        <div style={{ ...styles.parties, ...(mobile ? { gridTemplateColumns: '1fr' } : {}) }}>
                            <div style={styles.party}>
                                <div style={styles.partyHead}>{'공급자'}</div>
                                <div style={styles.partyRow}><span>{'상호'}</span><b>{SAMPLE_PARTNER.companyName}</b></div>
                                <div style={styles.partyRow}><span>{'사업자번호'}</span><span>{'123-45-67890'}</span></div>
                                <div style={styles.partyRow}><span>{'담당'}</span><span>{SAMPLE_PARTNER.managerName}</span></div>
                            </div>
                            <div style={styles.party}>
                                <div style={styles.partyHead}>{'공급받는자'}</div>
                                <div style={styles.partyRow}><span>{'상호'}</span><b>{lab.name}</b></div>
                                <div style={styles.partyRow}><span>{'사업자번호'}</span><span>{'987-65-43210'}</span></div>
                                <div style={styles.partyRow}><span>{'거래처 코드'}</span><span>{lab.code}</span></div>
                            </div>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ ...ui.table, fontSize: 12 }}>
                                <thead>
                                    <tr>
                                        <th style={styles.dth}>{'No'}</th>
                                        <th style={styles.dth}>{'출고일'}</th>
                                        <th style={styles.dth}>{'케이스ID'}</th>
                                        <th style={styles.dth}>{'종류'}</th>
                                        <th style={styles.dth}>{'치아'}</th>
                                        {!mobile && <th style={styles.dth}>{'내용'}</th>}
                                        <th style={{ ...styles.dth, textAlign: 'right' }}>{'수량'}</th>
                                        <th style={{ ...styles.dth, textAlign: 'right' }}>{'단가'}</th>
                                        <th style={{ ...styles.dth, textAlign: 'right' }}>{'금액'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((c, i) => (
                                        <tr key={c.id}>
                                            <td style={styles.dtd}>{i + 1}</td>
                                            <td style={styles.dtd}>{fmtMd(c.shippedAt)}</td>
                                            <td style={{ ...styles.dtd, fontFamily: 'ui-monospace, Menlo, monospace' }}>{c.caseNo}</td>
                                            <td style={styles.dtd}>{MF_KIND_LABEL[c.kind]}</td>
                                            <td style={styles.dtd}>{teethLabel(c.teeth, c.bridge)}</td>
                                            {!mobile && <td style={styles.dtd}>{detailSummary(c)}</td>}
                                            <td style={{ ...styles.dtd, textAlign: 'right' }}>{qtyOf(c)}</td>
                                            <td style={{ ...styles.dtd, textAlign: 'right' }}>{priceOf(c).toLocaleString('ko-KR')}</td>
                                            <td style={{ ...styles.dtd, textAlign: 'right', fontWeight: 600 }}>{(priceOf(c) * qtyOf(c)).toLocaleString('ko-KR')}</td>
                                        </tr>
                                    ))}
                                    {items.length === 0 && (
                                        <tr><td style={{ ...styles.dtd, textAlign: 'center', color: '#94a3b8' }} colSpan={9}>{'출고된 건이 없습니다'}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div style={styles.totals}>
                            <div style={styles.totalRow}><span>{'공급가액'}</span><span>{won(supply)}</span></div>
                            <div style={styles.totalRow}><span>{'부가세 (10%)'}</span><span>{won(vat)}</span></div>
                            <div style={{ ...styles.totalRow, ...styles.grand }}><span>{'합계'}</span><span>{won(supply + vat)}</span></div>
                        </div>
                    </div>
                    <p style={ui.cardSub}>{'같은 명세서가 기공소 덴클 화면에도 그대로 표시되고, 기공소는 「확인」 또는 「이의」를 누를 수 있습니다. 치과명·환자명은 들어가지 않습니다.'}</p>

                    {/* 이의 내용 — 기공소가 남긴 사유 */}
                    {status === 'disputed' && (
                        <div style={styles.disputeBox}>
                            <div style={styles.disputeHead}><MessageSquareWarning size={14} />{'기공소 이의'}</div>
                            <p style={{ margin: 0, fontSize: 13, color: '#7f1d1d', lineHeight: 1.5 }}>{PREV_DISPUTE[lab.id]}</p>
                            <p style={{ ...ui.muted, margin: 0 }}>{'항목을 고쳐 다시 보내면 기공소가 다시 확인합니다. 고친 내용은 아래 이력에 남습니다.'}</p>
                        </div>
                    )}

                    {/* 수정 이력 — 누가 언제 무엇을 했는지(지울 수 없음) */}
                    <div style={styles.historyBox}>
                        <div style={styles.disputeHead}><History size={14} color="#475569" /><span style={{ color: '#334155' }}>{'이력'}</span></div>
                        {history.map((h, i) => (
                            <div key={i} style={styles.historyRow}>
                                <span style={styles.historyAt}>{h.at}</span>
                                <span style={styles.historyWho}>{h.who}</span>
                                <span style={{ flex: 1 }}>{h.text}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div style={styles.modalFoot}>
                    <button type="button" style={ui.ghostBtn} onClick={() => notReady('PDF 저장')}><FileDown size={14} />{'PDF'}</button>
                    <button type="button" style={ui.ghostBtn} onClick={() => notReady('엑셀 저장')}><FileSpreadsheet size={14} />{'엑셀'}</button>
                    <span style={{ flex: 1 }} />
                    {/* 계산서 — 양측 확정 후에만. 과세(세금계산서)/면세(계산서)는 세무 확인 후 자동 선택(미확정) */}
                    <button
                        type="button"
                        style={{ ...ui.ghostBtn, ...(canInvoice ? {} : { opacity: 0.45, cursor: 'not-allowed' }) }}
                        disabled={!canInvoice}
                        title={canInvoice ? '' : '기공소가 확인해 양측 확정된 뒤에 발행할 수 있습니다'}
                        onClick={() => notReady('계산서 발행')}
                    >
                        <Receipt size={14} />{'계산서 발행'}
                    </button>
                    {status !== 'confirmed' && (
                        <button type="button" style={ui.primaryBtn} onClick={() => notReady('기공소에 보내기')}>
                            <Send size={14} />{status === 'draft' ? '발행하고 기공소에 보내기' : status === 'disputed' ? '고쳐서 다시 보내기' : '다시 보내기'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function PartnerStatement({ mobile }) {
    const [monthKey, setMonthKey] = useState('this');   // this / prev
    const [base, setBase] = useState(BASE_PRICE);
    const [exceptions, setExceptions] = useState(SAMPLE_EXCEPTIONS);
    const [priceOpen, setPriceOpen] = useState(false);
    const [docLabId, setDocLabId] = useState('');

    const now = new Date();
    const month = monthKey === 'this' ? new Date(now.getFullYear(), now.getMonth(), 1) : new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const all = useMemo(() => makeSampleCases(), []);
    // 지난달 = 같은 샘플을 한 달 앞으로 옮겨 보여줌(화면 배치 확인용)
    const shipped = useMemo(() => all.filter((c) => c.status === 'shipped')
        .map((c) => (monthKey === 'this' ? c : { ...c, shippedAt: new Date(c.shippedAt.getTime() - 30 * DAY) })), [all, monthKey]);
    const unshippedCount = monthKey === 'this' ? all.filter((c) => c.status === 'milling').length : 0;

    const priceOf = (c) => {
        const ex = exceptions.find((e) => e.labId === c.labId && e.kind === c.kind);
        return ex ? ex.price : (base[c.kind] || 0);
    };
    const byLab = SAMPLE_LABS.map((l) => {
        const items = shipped.filter((c) => c.labId === l.id).sort((a, b) => a.shippedAt - b.shippedAt);
        const supply = items.reduce((s, c) => s + priceOf(c) * qtyOf(c), 0);
        const vat = Math.round(supply * 0.1);
        return { lab: l, items, supply, vat, total: supply + vat, status: monthKey === 'this' ? 'draft' : (PREV_STATUS[l.id] || 'issued') };
    });
    const sum = byLab.reduce((a, r) => ({ count: a.count + r.items.length, supply: a.supply + r.supply, vat: a.vat + r.vat }), { count: 0, supply: 0, vat: 0 });
    const docRow = byLab.find((r) => r.lab.id === docLabId);
    const closeDay = lastDayOf(month);

    return (
        <div style={styles.col}>
            {/* 툴바 — 월·마감·단가표 */}
            <div style={ui.toolBar}>
                <select style={ui.select} value={monthKey} onChange={(e) => setMonthKey(e.target.value)}>
                    <option value="this">{`${ymOf(new Date(now.getFullYear(), now.getMonth(), 1))} (진행 중)`}</option>
                    <option value="prev">{ymOf(new Date(now.getFullYear(), now.getMonth() - 1, 1))}</option>
                </select>
                <span style={styles.closeInfo}>
                    <CalendarClock size={14} />
                    {monthKey === 'this' ? `매월 말일 마감 · ${fmtMd(closeDay)} 자동 생성` : `${fmtMd(closeDay)} 마감 완료`}
                </span>
                <span style={{ flex: 1 }} />
                <button type="button" style={ui.ghostBtn} onClick={() => setPriceOpen(true)}><Tag size={14} />{'단가표'}</button>
            </div>

            {/* 출고 미확인 경고 — 명세서 수량은 「출고」 클릭 기준 */}
            {unshippedCount > 0 && (
                <div style={styles.warn}>
                    <AlertTriangle size={15} />
                    <span>{`밀링 중인 케이스 ${unshippedCount}건은 「출고」로 바꿔야 이번 달 명세서에 들어갑니다. 출고 처리를 빠뜨리면 기공소 명세서와 수량이 어긋납니다.`}</span>
                </div>
            )}

            {/* 합계 카드 */}
            <div style={{ ...styles.sumRow, ...(mobile ? { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } : {}) }}>
                <div style={styles.sumCard}><span style={ui.muted}>{'출고 건수'}</span><b style={styles.sumNum}>{`${sum.count}건`}</b></div>
                <div style={styles.sumCard}><span style={ui.muted}>{'공급가액'}</span><b style={styles.sumNum}>{won(sum.supply)}</b></div>
                <div style={styles.sumCard}><span style={ui.muted}>{'부가세'}</span><b style={styles.sumNum}>{won(sum.vat)}</b></div>
                <div style={styles.sumCard}><span style={ui.muted}>{'합계'}</span><b style={{ ...styles.sumNum, color: '#4338ca' }}>{won(sum.supply + sum.vat)}</b></div>
            </div>

            {/* 기공소별 명세서 */}
            <div style={ui.tableWrap}>
                <table style={ui.table}>
                    <thead>
                        <tr>
                            <th style={ui.th}>{'기공소'}</th>
                            <th style={{ ...ui.th, textAlign: 'right' }}>{'출고'}</th>
                            {!mobile && <th style={{ ...ui.th, textAlign: 'right' }}>{'공급가액'}</th>}
                            {!mobile && <th style={{ ...ui.th, textAlign: 'right' }}>{'부가세'}</th>}
                            <th style={{ ...ui.th, textAlign: 'right' }}>{'합계'}</th>
                            <th style={ui.th}>{'상태'}</th>
                            {!mobile && <th style={ui.th} />}
                        </tr>
                    </thead>
                    <tbody>
                        {byLab.map((r) => (
                            <tr key={r.lab.id} style={{ cursor: 'pointer' }} onClick={() => setDocLabId(r.lab.id)}>
                                <td style={{ ...ui.td, fontWeight: 600 }}>{r.lab.name}</td>
                                <td style={ui.tdNum}>{`${r.items.length}건`}</td>
                                {!mobile && <td style={ui.tdNum}>{won(r.supply)}</td>}
                                {!mobile && <td style={{ ...ui.tdNum, color: '#64748b' }}>{won(r.vat)}</td>}
                                <td style={{ ...ui.tdNum, fontWeight: 700 }}>{won(r.total)}</td>
                                <td style={ui.td}><StatementBadge status={r.status} /></td>
                                {!mobile && (
                                    <td style={{ ...ui.td, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                        <div style={styles.btnRow}>
                                            <button type="button" style={styles.smallBtnPrimary} onClick={() => setDocLabId(r.lab.id)}>{'명세서 보기'}</button>
                                            <button type="button" style={styles.smallBtn} onClick={() => notReady('PDF')}><FileDown size={12} />{'PDF'}</button>
                                            <button type="button" style={styles.smallBtn} onClick={() => notReady('엑셀')}><FileSpreadsheet size={12} />{'엑셀'}</button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p style={ui.muted}>{'케이스를 「출고」로 바꾸는 순간 한 줄씩 쌓이고, 마감일에 기공소별로 자동 생성됩니다. 기공소가 확인해 양측 확정되면 계산서를 발행합니다(과세·면세 구분은 세무 확인 후 자동 적용).'}</p>

            {priceOpen && <PriceModal base={base} setBase={setBase} exceptions={exceptions} setExceptions={setExceptions} onClose={() => setPriceOpen(false)} />}
            {docRow && (
                <StatementDoc lab={docRow.lab} items={docRow.items} month={month} status={docRow.status} priceOf={priceOf} onClose={() => setDocLabId('')} mobile={mobile} />
            )}
        </div>
    );
}

const styles = {
    col: { display: 'flex', flexDirection: 'column', gap: 10 },
    disputeBox: { padding: 12, borderRadius: 10, backgroundColor: '#fef2f2', border: '1px solid #fecaca', display: 'flex', flexDirection: 'column', gap: 6 },
    disputeHead: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#b91c1c' },
    historyBox: { padding: 12, borderRadius: 10, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 6 },
    historyRow: { display: 'flex', gap: 10, fontSize: 12, color: '#334155' },
    historyAt: { color: '#94a3b8', fontFamily: 'ui-monospace, Menlo, monospace', whiteSpace: 'nowrap' },
    historyWho: { fontWeight: 700, minWidth: 40 },
    closeInfo: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#64748b' },
    warn: {
        display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 10,
        backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 13, lineHeight: 1.5,
    },
    sumRow: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 },
    sumCard: { ...ui.card, padding: 14, display: 'flex', flexDirection: 'column', gap: 4 },
    sumNum: { fontSize: 18, fontWeight: 800, color: '#0f172a' },
    btnRow: { display: 'inline-flex', gap: 4 },
    smallBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 8px', border: '1px solid #cbd5e1',
        borderRadius: 7, backgroundColor: '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
    },
    smallBtnPrimary: {
        display: 'inline-flex', alignItems: 'center', gap: 3, padding: '4px 10px', border: '1px solid #c7d2fe',
        borderRadius: 7, backgroundColor: '#eef2ff', color: '#4338ca', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    },
    // 모달
    overlay: {
        position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.35)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    },
    modal: {
        width: '100%', maxHeight: '92vh', backgroundColor: '#fff', borderRadius: 14,
        boxShadow: '0 20px 50px rgba(15,23,42,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    },
    modalHead: { display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: '1px solid #e2e8f0' },
    modalTitle: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 800, color: '#0f172a' },
    modalBody: { padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 },
    modalFoot: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '12px 18px', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' },
    closeBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 },
    secTitle: { fontSize: 13, fontWeight: 700, color: '#334155' },
    priceRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f1f5f9' },
    priceInput: { width: 110, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, textAlign: 'right' },
    addRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 },
    iconBtn: {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26,
        border: '1px solid #e2e8f0', borderRadius: 7, backgroundColor: '#fff', cursor: 'pointer', color: '#94a3b8',
    },
    // 거래명세서 문서
    doc: { border: '1px solid #cbd5e1', borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column', gap: 12, backgroundColor: '#fff' },
    docTitle: { textAlign: 'center', fontSize: 20, fontWeight: 800, color: '#0f172a', letterSpacing: 4 },
    docMeta: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b' },
    parties: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
    party: { border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 4 },
    partyHead: { fontSize: 12, fontWeight: 800, color: '#4338ca', marginBottom: 2 },
    partyRow: { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#334155' },
    dth: { textAlign: 'left', padding: '7px 8px', backgroundColor: '#f8fafc', color: '#475569', fontWeight: 700, borderBottom: '1px solid #cbd5e1', whiteSpace: 'nowrap' },
    dtd: { padding: '7px 8px', borderBottom: '1px solid #f1f5f9', color: '#1e293b', whiteSpace: 'nowrap' },
    totals: { alignSelf: 'flex-end', width: 260, maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: 4 },
    totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#334155' },
    grand: { borderTop: '1px solid #cbd5e1', paddingTop: 6, fontSize: 15, fontWeight: 800, color: '#0f172a' },
};

export { StatementDoc, PriceModal };
export default PartnerStatement;
